import { useRef } from 'react'
import clsx from 'clsx'

type TableView = 'table' | 'sources'

/** Workspace view tabs own their tab styling and keyboard navigation. */
export default function TableViewTabs({
  value,
  onChange,
  id,
  documentBased = true,
  disabled = false,
}: {
  value: TableView
  onChange: (view: TableView) => void
  id: string
  documentBased?: boolean
  disabled?: boolean
}) {
  const buttons = useRef(new Map<TableView, HTMLButtonElement>())
  const views: TableView[] = documentBased ? ['table', 'sources'] : ['table']
  return (
    <div role="tablist" aria-label="Table content" className="relative z-10 flex self-start gap-1">
      {views.map((view) => (
        <button
          key={view}
          ref={(button) => {
            if (button) buttons.current.set(view, button)
            else buttons.current.delete(view)
          }}
          disabled={disabled}
          type="button"
          role="tab"
          id={`${id}-${view}-tab`}
          aria-controls={`${id}-${view}-panel`}
          aria-selected={value === view}
          tabIndex={value === view ? 0 : -1}
          className={clsx(
            '-mb-px flex h-7 items-center rounded-t-md border border-brand-neutral-200 px-3 text-sm font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-500',
            value === view
              ? 'border-b-brand-neutral-50 bg-brand-neutral-50 text-brand-800'
              : 'bg-brand-neutral-100 text-brand-neutral-700 hover:bg-brand-neutral-200'
          )}
          onClick={() => onChange(view)}
          onKeyDown={(event) => {
            if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return
            event.preventDefault()
            const next = event.key === 'Home' ? views[0] : event.key === 'End' ? views[views.length - 1] : views[(views.indexOf(view) + 1) % views.length]
            onChange(next)
            buttons.current.get(next)?.focus()
          }}
        >
          {view === 'table' ? 'Table' : 'Source Files'}
        </button>
      ))}
    </div>
  )
}
